import { useState, useEffect } from "react";

const Header = () => {
  const [expanded, setExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    // Initial check
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const toggleMenu = () => {
    setExpanded(!expanded);
  };

  const handleNavClick = (
    e,
    sectionId
  ) => {
    e.preventDefault();
    const section = document.getElementById(sectionId);
    const headerHeight = document.querySelector('header')?.offsetHeight || 80;
    
    if (section) {
      const targetPosition = section.getBoundingClientRect().top + window.pageYOffset - headerHeight;
      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
      setExpanded(false); // Close mobile menu when navigating
    }
  };
  return (
    <header className={`py-4 sm:py-6 fixed top-0 left-0 right-0 w-full z-50 transition-all duration-300 ${
      scrolled ? "bg-black/95 shadow-md backdrop-blur-sm" : "bg-black/80"
    }`}>
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <div className="shrink-0">
            <a href="/" title="InterConnect" className="flex items-center">
              <div className="flex items-center">
                <svg
                  className="w-10 h-10 text-blue-500"
                  viewBox="0 0 64 64"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="32" cy="32" r="30" fill="url(#paint0_linear)" />
                  <path
                    d="M20 32H44"
                    stroke="white"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                  <path
                    d="M32 20V44"
                    stroke="white"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r="8"
                    stroke="white"
                    strokeWidth="4"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear"
                      x1="16"
                      y1="16"
                      x2="48"
                      y2="48"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#4A00E0" />
                      <stop offset="1" stopColor="#00C2FF" />
                    </linearGradient>
                  </defs>
                </svg>
                <span className="ml-2 text-xl font-bold text-white">
                  InterConnect
                </span>
              </div>
            </a>
          </div>

          <div className="flex md:hidden">
            <button
              type="button"
              className="text-white"
              onClick={toggleMenu}
              aria-expanded={expanded}
            >
              {!expanded ? (
                <span aria-hidden="true">
                  <svg
                    className="w-7 h-7"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </span>
              ) : (
                <span aria-hidden="true">
                  <svg
                    className="w-7 h-7"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </span>
              )}
            </button>
          </div>

          <nav className="hidden ml-10 mr-auto space-x-10 lg:ml-20 lg:space-x-12 md:flex md:items-center md:justify-start">
            <a
              href="#how-it-works"
              title="How it Works"
              className="text-base font-normal text-gray-400 transition-all duration-200 hover:text-white"
              onClick={(e) => handleNavClick(e, "how-it-works")}
            >
              How it Works
            </a>

            <a
              href="#features"
              title="Features"
              className="text-base font-normal text-gray-400 transition-all duration-200 hover:text-white"
              onClick={(e) => handleNavClick(e, "features")}
            >
              Features
            </a>

            <a
              href="#testimonials"
              title="Success Stories"
              className="text-base font-normal text-gray-400 transition-all duration-200 hover:text-white"
              onClick={(e) => handleNavClick(e, "testimonials")}
            >
              Success Stories
            </a>

            <a
              href="#faq"
              title="FAQ"
              className="text-base font-normal text-gray-400 transition-all duration-200 hover:text-white"
              onClick={(e) => handleNavClick(e, "faq")}
            >
              FAQ
            </a>
          </nav>

          <div className="relative hidden md:items-center md:justify-center md:inline-flex group">
            <div className="absolute transition-all duration-200 rounded-full -inset-px bg-gradient-to-r from-cyan-500 to-purple-500 group-hover:shadow-lg group-hover:shadow-cyan-500/50"></div>
            <a
              href="/login"
              title="Login as Student"
              className="relative inline-flex items-center justify-center px-6 py-2 text-base font-normal text-white bg-black border border-transparent rounded-full"
              role="button"
            >
              Login
            </a>
          </div>
        </div>

        {expanded && (
          <nav className="mobile-menu absolute top-full left-0 right-0 bg-black/95 backdrop-blur-sm mt-0 border-t border-gray-800">
            <div className="flex flex-col pt-8 pb-4 space-y-6 px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
              <a
                href="#how-it-works"
                title="How it Works"
                className="text-base font-normal text-gray-400 transition-all duration-200 hover:text-white"
                onClick={(e) => handleNavClick(e, "how-it-works")}
              >
                How it Works
              </a>

              <a
                href="#features"
                title="Features"
                className="text-base font-normal text-gray-400 transition-all duration-200 hover:text-white"
                onClick={(e) => handleNavClick(e, "features")}
              >
                Features
              </a>

              <a
                href="#testimonials"
                title="Success Stories"
                className="text-base font-normal text-gray-400 transition-all duration-200 hover:text-white"
                onClick={(e) => handleNavClick(e, "testimonials")}
              >
                Success Stories
              </a>

              <a
                href="#faq"
                title="FAQ"
                className="text-base font-normal text-gray-400 transition-all duration-200 hover:text-white"
                onClick={(e) => handleNavClick(e, "faq")}
              >
                FAQ
              </a>

              <div className="relative inline-flex items-center justify-center group">
                <div className="absolute transition-all duration-200 rounded-full -inset-px bg-gradient-to-r from-cyan-500 to-purple-500 group-hover:shadow-lg group-hover:shadow-cyan-500/50"></div>
                <a
                  href="/login"
                  title="Login as Student"
                  className="relative inline-flex items-center justify-center w-full px-6 py-2 text-base font-normal text-white bg-black border border-transparent rounded-full"
                  role="button"
                >
                  Login
                </a>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
